import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import { ACTIVE_SESSIONS, LOGIN_HISTORY } from './settingsData';

export default function SecuritySection({ isAdmin, showToast }) {
  const [sessions, setSessions] = useState(ACTIVE_SESSIONS);
  const [loginPage, setLoginPage] = useState(1);
  const [sessionTimeout, setSessionTimeout] = useState('1 hour');
  const [requireReauth, setRequireReauth] = useState(true);
  const [ipAllowlist, setIpAllowlist] = useState(false);
  const [ipRanges, setIpRanges] = useState('');

  const PAGE_SIZE = 5;
  const totalPages = Math.ceil(LOGIN_HISTORY?.length / PAGE_SIZE);
  const pagedHistory = LOGIN_HISTORY?.slice((loginPage - 1) * PAGE_SIZE, loginPage * PAGE_SIZE);

  const revokeSession = (id) => {
    setSessions(prev => prev?.filter(s => s?.id !== id));
    showToast('Session revoked');
  };

  const revokeAll = () => {
    setSessions(prev => prev?.filter(s => s?.isCurrent));
    showToast('All other sessions revoked');
  };

  const inputCls = 'w-full px-3 py-2 border border-slate-200 rounded-xl text-sm text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-[#3b4c4c]/30 focus:border-[#3b4c4c] transition-colors';

  return (
    <div>
      {/* Active Sessions */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm mb-6">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Active Sessions</h3>
            <p className="text-sm text-slate-500">{sessions?.length} active session{sessions?.length !== 1 ? 's' : ''}</p>
          </div>
          {sessions?.filter(s => !s?.isCurrent)?.length > 0 && (
            <button
              onClick={revokeAll}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border border-red-200 text-red-600 bg-red-50 hover:bg-red-100 transition-colors"
            >
              <Icon name="LogOut" size={15} />
              Revoke All Other Sessions
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100">
                {['Device', 'Location', 'IP Address', 'Last Active', 'Actions']?.map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sessions?.map(session => (
                <tr key={session?.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <Icon name={session?.device?.includes('iPhone') || session?.device?.includes('Android') ? 'Smartphone' : 'Monitor'} size={16} color="#64748b" />
                      <div>
                        <p className="text-sm font-medium text-slate-800">{session?.device}</p>
                        {session?.isCurrent && (
                          <span className="text-xs font-semibold text-green-600 bg-green-100 px-2 py-0.5 rounded-full">Current session</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{session?.location}</td>
                  <td className="px-4 py-3"><code className="text-xs font-mono text-slate-600 bg-slate-100 px-2 py-1 rounded-lg">{session?.ip}</code></td>
                  <td className="px-4 py-3 text-sm text-slate-600">{session?.lastActive}</td>
                  <td className="px-4 py-3">
                    {!session?.isCurrent && (
                      <button
                        onClick={() => revokeSession(session?.id)}
                        className="text-xs font-semibold text-red-600 hover:text-red-700 hover:underline"
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Login History */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm mb-6">
        <div className="px-6 py-4 border-b border-slate-100">
          <h3 className="text-base font-semibold text-slate-900">Login History</h3>
          <p className="text-sm text-slate-500">Recent sign-in activity for your account.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100">
                {['Date / Time', 'Device', 'Location', 'IP Address', 'Status']?.map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pagedHistory?.map(entry => (
                <tr key={entry?.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                  <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap">{entry?.datetime}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{entry?.device}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{entry?.location}</td>
                  <td className="px-4 py-3"><code className="text-xs font-mono text-slate-600 bg-slate-100 px-2 py-1 rounded-lg">{entry?.ip}</code></td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                      entry?.status === 'Successful' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                    }`}>{entry?.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="flex items-center justify-between px-6 py-3 border-t border-slate-100">
          <p className="text-xs text-slate-500">Showing {(loginPage - 1) * PAGE_SIZE + 1}–{Math.min(loginPage * PAGE_SIZE, LOGIN_HISTORY?.length)} of {LOGIN_HISTORY?.length}</p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setLoginPage(p => Math.max(1, p - 1))}
              disabled={loginPage === 1}
              className="p-1.5 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Icon name="ChevronLeft" size={14} />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)?.map(p => (
              <button
                key={p}
                onClick={() => setLoginPage(p)}
                className={`w-7 h-7 rounded-lg text-xs font-medium transition-colors ${
                  loginPage === p ? 'bg-[#3b4c4c] text-white' : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setLoginPage(p => Math.min(totalPages, p + 1))}
              disabled={loginPage === totalPages}
              className="p-1.5 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Icon name="ChevronRight" size={14} />
            </button>
          </div>
        </div>
      </div>
      {/* Security Settings */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
        <h3 className="text-base font-semibold text-slate-900 mb-1">Security Settings</h3>
        <p className="text-sm text-slate-500 mb-5">Configure session behaviour and access controls.</p>
        <div className="border-t border-slate-100 pt-5 space-y-5">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <label className="sm:w-64 text-sm font-medium text-slate-700 flex-shrink-0">Session Timeout</label>
            <select value={sessionTimeout} onChange={e => setSessionTimeout(e?.target?.value)} className="flex-1 max-w-xs px-3 py-2 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#3b4c4c]/30">
              <option>15 min</option>
              <option>30 min</option>
              <option>1 hour</option>
              <option>4 hours</option>
              <option>8 hours</option>
            </select>
          </div>

          <div className="flex items-center justify-between py-3 border-t border-slate-50">
            <div>
              <p className="text-sm font-medium text-slate-700">Require Re-authentication for Sensitive Actions</p>
              <p className="text-xs text-slate-400">Prompt for password before critical operations</p>
            </div>
            <button
              onClick={() => setRequireReauth(!requireReauth)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                requireReauth ? 'bg-[#3b4c4c]' : 'bg-slate-200'
              }`}
            >
              <span className="inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform" style={{ transform: requireReauth ? 'translateX(22px)' : 'translateX(2px)' }} />
            </button>
          </div>

          <div className="py-3 border-t border-slate-50">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  IP Allowlist
                  {!isAdmin && <Icon name="Lock" size={13} color="#94a3b8" title="Admin access required" />}
                </p>
                <p className="text-xs text-slate-400">Restrict access to specific IP ranges (CIDR notation)</p>
              </div>
              <button
                onClick={() => isAdmin && setIpAllowlist(!ipAllowlist)}
                disabled={!isAdmin}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:cursor-not-allowed ${
                  ipAllowlist ? 'bg-[#3b4c4c]' : 'bg-slate-200'
                }`}
              >
                <span className="inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform" style={{ transform: ipAllowlist ? 'translateX(22px)' : 'translateX(2px)' }} />
              </button>
            </div>
            {ipAllowlist && (
              <textarea
                value={ipRanges}
                onChange={e => setIpRanges(e?.target?.value)}
                rows={3}
                placeholder="e.g. 192.168.1.0/24&#10;10.0.0.0/8"
                className={`${inputCls} resize-none font-mono text-xs`}
              />
            )}
          </div>
        </div>

        <div className="flex justify-end pt-4 border-t border-slate-100 mt-4">
          <button
            onClick={() => showToast('Security settings saved')}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-[#3b4c4c] hover:bg-[#162d4a] transition-colors shadow-sm"
          >
            <Icon name="Save" size={15} />
            Save Security Settings
          </button>
        </div>
      </div>
    </div>
  );
}
